import type { WorkItemView } from '@/api/types';

/**
 * Parse the backend's workItemKey into a (scope, id) tuple suitable for URL paths.
 * The key format is "jira:KEY" for Jira-linked work items and "pr:UUID" for standalone PRs.
 */
export function parseWorkItemKey(workItemKey: string): { scope: 'jira' | 'pr'; id: string } {
  if (workItemKey.startsWith('jira:')) {
    return { scope: 'jira', id: workItemKey.slice('jira:'.length) };
  }
  if (workItemKey.startsWith('pr:')) {
    return { scope: 'pr', id: workItemKey.slice('pr:'.length) };
  }
  // Defensive fallback — treat anything else as Jira so the URL is still well-formed.
  return { scope: 'jira', id: workItemKey };
}

export function workItemRoute(item: { workItemKey: string; author: string }): string {
  const { scope, id } = parseWorkItemKey(item.workItemKey);
  return `/work-items/${scope}/${encodeURIComponent(id)}/${encodeURIComponent(item.author)}`;
}

/**
 * Heading + subtitle for a work item row. Jira-linked items lead with the issue key and
 * fall back to its cached summary; standalone PRs show the PR title so the row is still
 * recognizable when no Jira issue exists.
 */
export function workItemDisplayLabel(item: WorkItemView): { heading: string; subtitle: string | null } {
  if (item.issueKey) {
    return {
      heading: item.issueKey,
      subtitle: item.issueSummary ?? null,
    };
  }
  const firstPr = item.pullRequests[0];
  if (firstPr) {
    return {
      heading: `PR #${firstPr.prNumber}`,
      subtitle: firstPr.prTitle,
    };
  }
  return { heading: 'Standalone', subtitle: null };
}
